import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, Repeat, CheckCircle2, XCircle, Clock } from "lucide-react";
import { WEEKDAYS } from "./constants";

function fmtTime(iso) { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }

export default function Agenda() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [weekday, setWeekday] = useState(0);
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const { data: d } = await api.get("/coach/assistant/agenda"); setData(d); } catch { /* noop */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const addRecurring = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try { await api.post("/coach/assistant/agenda/recurring", { title, weekday: Number(weekday), time }); setTitle(""); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const delRecurring = async (id) => { try { await api.delete(`/coach/assistant/agenda/recurring/${id}`); await load(); } catch (err) { toast.error(apiError(err)); } };

  if (loading) return <Skeleton className="h-64 rounded-2xl" />;
  const events = data?.app_events || [];
  const recurring = data?.recurring || [];
  const outlookOn = data?.outlook_connected;

  return (
    <div className="space-y-4" data-testid="aipa-agenda">
      <h2 className="font-display text-xl text-foreground">{t("aipa.agenda_title")}</h2>

      <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${outlookOn ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-border bg-secondary/50 text-muted-foreground"}`} data-testid="aipa-outlook-status">
        {outlookOn ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
        {outlookOn ? t("aipa.agenda_outlook_on") : t("aipa.agenda_outlook_off")}
      </div>

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="aipa-agenda-events">
        <div className="mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /><p className="text-sm font-semibold text-foreground">{t("aipa.agenda_events")}</p></div>
        {events.length === 0 ? <p className="text-sm text-muted-foreground">{t("aipa.agenda_no_events")}</p> : (
          <div className="space-y-2">{events.map((e, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-background p-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{fmtTime(e.scheduled_at)}</span>
              <span className="truncate text-sm text-muted-foreground">{e.title || "—"}</span>
            </div>
          ))}</div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="aipa-agenda-recurring">
        <div className="mb-3 flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /><p className="text-sm font-semibold text-foreground">{t("aipa.agenda_recurring")}</p></div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("aipa.recurring_title_ph")} className="min-w-[160px] flex-1" data-testid="aipa-recurring-title" />
          <select value={weekday} onChange={(e) => setWeekday(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground" data-testid="aipa-recurring-weekday">
            {WEEKDAYS.map((d) => <option key={d} value={d}>{t(`aipa.wd.${d}`)}</option>)}
          </select>
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-28" data-testid="aipa-recurring-time" />
          <Button className="gap-1.5 rounded-full" disabled={saving} onClick={addRecurring} data-testid="aipa-recurring-add"><Plus className="h-4 w-4" /> {t("aipa.recurring_save")}</Button>
        </div>
        {recurring.length === 0 ? <p className="text-sm text-muted-foreground">{t("aipa.agenda_recurring_empty")}</p> : (
          <div className="space-y-2">{recurring.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
              <span className="text-sm text-foreground">{t(`aipa.wd.${r.weekday}`)} · {r.time} · {r.title}</span>
              <button onClick={() => delRecurring(r.id)} data-testid={`aipa-recurring-del-${r.id}`} className="rounded-lg border border-border p-1.5 text-rose-500 hover:bg-accent"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}</div>
        )}
      </section>
    </div>
  );
}
